
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { YouTubeChannel } from '../types';
import { useToast } from '@/components/ui/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { addSocialProfile } from '@/lib/supabase/social';
import { supabase } from '@/lib/supabase/client';

interface YouTubeProfileEditorProps {
  channels: YouTubeChannel[];
  accessToken: string;
  refreshToken: string;
  tokenExpiry: number;
  onProfileSaved: () => void;
  onCancel: () => void;
  onShowChannelSelection: () => void;
}

const YouTubeProfileEditor = ({
  channels,
  accessToken,
  refreshToken,
  tokenExpiry,
  onProfileSaved,
  onCancel,
  onShowChannelSelection
}: YouTubeProfileEditorProps) => {
  const { toast } = useToast();
  const { user } = useAuth();
  const [selectedChannel, setSelectedChannel] = useState<string>("");
  const [profileName, setProfileName] = useState<string>("My YouTube Channel");
  const [savingProfile, setSavingProfile] = useState<boolean>(false);

  const handleSaveProfile = async () => {
    if (!selectedChannel || !profileName) {
      toast({
        title: 'Missing information',
        description: 'Please select a channel and provide a name for the profile',
        variant: 'destructive',
      });
      return;
    }
    
    setSavingProfile(true);
    
    try {
      // Get organization ID
      const { data: orgData, error: orgError } = await supabase
        .from('organizations')
        .select('id')
        .single();
      
      if (orgError) throw new Error(`Error: ${orgError.message}`);
      if (!orgData?.id) throw new Error('No organization found');
      
      // Find selected channel
      const channel = channels.find(c => c.id === selectedChannel);
      if (!channel) throw new Error('Channel not found');
      
      // Create social profile
      const profileData = {
        organization_id: orgData.id,
        name: profileName,
        platform: 'youtube',
        profile_url: `https://www.youtube.com/channel/${selectedChannel}`,
        stats: channel.subscriberCount?.toString() || '0',
        created_by: user?.id || '',
        youtube_channel_id: selectedChannel,
        youtube_access_token: accessToken,
        youtube_refresh_token: refreshToken,
        youtube_token_expiry: tokenExpiry,
        type: 'social',
        // Ajout des informations du compte
        account_id: selectedChannel,
        account_name: channel.title,
        account_description: channel.description || '',
        account_avatar: channel.thumbnailUrl || '',
        // Ajout des statistiques
        follower_count: channel.subscriberCount || 0,
        post_count: channel.videoCount || 0,
        total_views: 0 // YouTube API ne fournit pas facilement le nombre total de vues dans cette requête
      };
      
      const result = await addSocialProfile(profileData);
      
      if (!result.success) {
        throw new Error(result.error || 'Error creating profile');
      }
      
      toast({
        title: 'Success',
        description: `Profile "${profileName}" added successfully.`,
      });
      
      onProfileSaved();
    } catch (error) {
      console.error('Error saving:', error);
      
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : "Could not save profile.",
        variant: 'destructive',
      });
    } finally {
      setSavingProfile(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm overflow-hidden">
      <div className="p-4 border-b border-gray-100 flex items-center">
        <Button variant="ghost" size="icon" onClick={onCancel} className="mr-2">
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h2 className="text-lg font-medium">Add a YouTube channel</h2>
      </div>
      
      <div className="p-6">
        <div className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="profile-name">Profile name</Label>
            <Input 
              id="profile-name" 
              value={profileName} 
              onChange={(e) => setProfileName(e.target.value)}
              placeholder="Profile name" 
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="channel">Select a channel</Label>
            <Select 
              value={selectedChannel} 
              onValueChange={(value) => {
                setSelectedChannel(value);
                // Update profile name with channel title
                const channel = channels.find(c => c.id === value);
                if (channel) {
                  setProfileName(channel.title);
                }
              }}
            >
              <SelectTrigger id="channel">
                <SelectValue placeholder="Select a channel" />
              </SelectTrigger>
              <SelectContent>
                {channels.map((channel) => (
                  <SelectItem key={channel.id} value={channel.id}>
                    {channel.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {channels.length === 0 && (
              <p className="text-sm text-yellow-600">No YouTube channels found for your account.</p>
            )}
            {channels.length > 1 && (
              <Button 
                variant="outline" 
                size="sm" 
                className="mt-2"
                onClick={onShowChannelSelection}
              >
                View all available channels
              </Button>
            )}
          </div>
          
          {selectedChannel && (
            <div>
              <p className="text-sm text-gray-500 mb-2">Channel information:</p>
              {channels.find(c => c.id === selectedChannel)?.thumbnailUrl && (
                <img 
                  src={channels.find(c => c.id === selectedChannel)?.thumbnailUrl} 
                  alt="Channel thumbnail" 
                  className="w-16 h-16 rounded-full mb-2"
                />
              )}
              <p className="text-sm">
                <span className="font-medium">Subscribers:</span> {channels.find(c => c.id === selectedChannel)?.subscriberCount?.toLocaleString() || 'N/A'}
              </p>
              <p className="text-sm">
                <span className="font-medium">Videos:</span> {channels.find(c => c.id === selectedChannel)?.videoCount?.toLocaleString() || 'N/A'}
              </p>
            </div>
          )}
        </div>
        
        <div className="pt-6 flex justify-end space-x-4">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSaveProfile}
            disabled={!selectedChannel || !profileName || savingProfile}
          >
            {savingProfile ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : 'Save profile'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default YouTubeProfileEditor;
